import React, { FC, memo } from 'react';
import { M11HeroModule } from '~/lib/data-contract';
import styles from './m11hero.module.css';
import HeroMedia from '../hero-media/hero-media.component';
import BlockWrapper from '../block-wrapper/block-wrapper.component';
import clsx from 'clsx';
import { isDarkColor, getContrastColor } from '~/themes/cms-color-helpers';
import { CategoryCardMedia, CategoryCardText, CMSLink } from '~/shared/components';
import { queries } from '~/theme';

const getImageSizes = (columnsCount = 1) => {
    switch (columnsCount) {
        case 1:
            return `${queries.belowSm} 100vw, 100vw`;
        case 2:
            return `${queries.belowSm} 100vw, ${queries.belowLg} 50vw, 33vw`;
        case 3:
            return `${queries.belowSm} 100vw, ${queries.belowLg} 33vw, 33vw`;
        case 4:
        default:
            return `${queries.belowSm} 100vw, ${queries.belowLg} 25vw, 33vw`;
    }
};

const M11Hero: FC<M11HeroModule> = memo((props) => {
    const {
        categories,
        hideDesktop,
        hideMobile,
        spacingTop,
        height,
        backgroundColor,
        text,
        headline,
    } = props;

    const [mainCategory, ...restCategories] = categories || [];
    const headlineText = headline || mainCategory?.title || '';
    const subHeadlineText = text || mainCategory?.text || '';

    const hasSideContent = restCategories.length > 0;

    const contrastBackgroundColor = getContrastColor(backgroundColor);
    const isDarkShade = isDarkColor(backgroundColor || '');
    const hasLink = Boolean(mainCategory?.callToAction?.url);

    return (
        <BlockWrapper
            hideMobile={hideMobile}
            hideDesktop={hideDesktop}
            spacingTop={spacingTop}
            height={height}
            backgroundColor={backgroundColor}
            className={clsx(hasSideContent ? styles.rootSplit : styles.root)}
        >
            <CMSLink
                href={mainCategory?.callToAction?.url || ''}
                title={mainCategory?.callToAction?.title || ''}
                className={clsx(styles.heroContent, hasLink && 'hover-effect-underline-center')}
            >
                <div className={styles.mediaContainer}>
                    <HeroMedia
                        image={mainCategory?.image}
                        size={restCategories?.length ? 'split' : 'full'}
                        video={mainCategory?.video}
                    />
                </div>
                <CategoryCardText
                    headline={headlineText}
                    text={subHeadlineText}
                    backgroundColor={contrastBackgroundColor}
                    isDarkShade={isDarkShade}
                    withLink={hasLink}
                    titleAs="h3"
                    className={styles.categoryTextBlock}
                    isMainCategory
                    ctaText={mainCategory?.callToAction?.text || mainCategory?.callToAction?.title}
                />
            </CMSLink>
            {hasSideContent && (
                <div className={styles.sideContent}>
                    {restCategories.map((cat, i) => {
                        const categoryHasLink = Boolean(cat.callToAction?.url);
                        return (
                            <CMSLink
                                href={cat.callToAction?.url || ''}
                                title={cat.callToAction?.title || ''}
                                key={`${(cat as any).id}-${i}`}
                                className={clsx(
                                    styles.category,
                                    categoryHasLink && 'hover-effect-underline-center'
                                )}
                            >
                                <div className={styles.categoryImage}>
                                    <CategoryCardMedia
                                        image={cat.image}
                                        video={cat.video}
                                        imgSizes={getImageSizes(restCategories.length)}
                                        is4by5
                                    />
                                </div>
                                <CategoryCardText
                                    headline={cat.title}
                                    text={cat.text}
                                    backgroundColor={contrastBackgroundColor}
                                    isDarkShade={isDarkShade}
                                    withLink={categoryHasLink}
                                    titleAs="h4"
                                    className={styles.categoryTextBlock}
                                />
                            </CMSLink>
                        );
                    })}
                </div>
            )}
        </BlockWrapper>
    );
});

M11Hero.displayName = 'M11Hero';

export default M11Hero;
